import React, { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, Globe, CheckCircle, XCircle, ArrowUpRight, ShoppingCart } from "@phosphor-icons/react";
import { motion } from "framer-motion";

export default function DomainScout() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const scan = async (e) => {
    e?.preventDefault();
    setError("");
    if (!q.trim()) return;
    setLoading(true);
    setResults([]);
    try {
      const { data } = await api.get("/tools/domain-check", { params: { query: q.trim() } });
      setResults(data.results);
    } catch (e) {
      setError(e.response?.data?.detail || "Scout offline");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="surface-card p-6 md:p-8" data-testid="domain-scout">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 border border-[#FFD700]/40 flex items-center justify-center flex-shrink-0">
          <Globe size={22} weight="duotone" className="text-[#FFD700]" />
        </div>
        <div>
          <div className="overline mb-1">// Domain Bridge</div>
          <h3 className="text-xl font-bold text-white">Sovereign Domain Scout</h3>
          <p className="text-sm text-white/60 font-light mt-1">
            Director's voice: "Your domain is your call-sign. Let's lock one before someone else does."
          </p>
        </div>
      </div>

      <form onSubmit={scan} className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            data-testid="domain-scout-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="yourbrand or yourbrand.com"
            className="pl-10 bg-[#0c0c0c] border-white/10 rounded-none h-12 text-white"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !q.trim()}
          data-testid="domain-scout-scan-btn"
          className="btn-gold px-6 py-3 text-xs whitespace-nowrap disabled:opacity-50"
        >
          {loading ? "Scanning..." : "Scan Domain"}
        </button>
      </form>

      {error && (
        <div className="text-sm text-red-400 mb-4 font-mono text-xs">{error}</div>
      )}

      {results.length > 0 && (
        <div className="space-y-2" data-testid="domain-scout-results">
          {results.map((r, i) => (
            <motion.div
              key={r.domain}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              className={`flex items-center justify-between p-3 border ${
                r.available === true
                  ? "border-[#FFD700]/30 bg-[#FFD700]/5"
                  : r.available === false
                  ? "border-white/5 opacity-60"
                  : "border-white/10"
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {r.available === true ? (
                  <CheckCircle size={20} weight="duotone" className="text-[#FFD700] flex-shrink-0" />
                ) : r.available === false ? (
                  <XCircle size={20} weight="duotone" className="text-white/40 flex-shrink-0" />
                ) : (
                  <Globe size={20} weight="duotone" className="text-white/50 flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="font-mono text-sm text-white truncate">{r.domain}</div>
                  <div className={`text-[0.65rem] font-mono uppercase tracking-widest ${
                    r.available === true ? "text-[#FFD700]" : "text-white/40"
                  }`}>
                    {r.available === true ? "Likely Available" : r.available === false ? "Taken" : "Unknown"}
                  </div>
                </div>
              </div>
              <a
                href={r.purchase_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`domain-buy-${r.domain.replace(/\./g, "-")}`}
                className="btn-ghost px-3 py-2 text-[0.65rem] flex items-center gap-2 flex-shrink-0"
              >
                <ShoppingCart size={12} />
                {r.available === true ? "Secure" : "Check"}
                <ArrowUpRight size={10} />
              </a>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
